class Collection {
  constructor() {
    this.currentCollection = null;
    this.sidebarItems = null;
    this.isLoading = false;

    this.init();
  }

  init() {
    // Get the current collection handle from the URL
    this.currentCollection = this.getCurrentCollectionHandle();

    // Find all sidebar items
    this.sidebarItems = document.querySelectorAll(
      ".collection-sidebar__item-link"
    );

    if (this.sidebarItems.length > 0) {
      this.bindEvents();
    }
  }

  getCurrentCollectionHandle() {
    // Extract collection handle from URL
    const path = window.location.pathname;
    const match = path.match(/\/collections\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  bindEvents() {
    this.sidebarItems.forEach((item) => {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleSidebarClick(e);
      });
    });
  }

  async handleSidebarClick(e) {
    const link = e.currentTarget;
    const url = link.getAttribute("href");

    if (!url || this.isLoading) return;

    // Extract collection handle from URL
    const match = url.match(/\/collections\/([^\/\?]+)/);
    if (!match) return;

    const collectionHandle = match[1];

    // Don't reload if it's the same collection
    if (collectionHandle === this.currentCollection) return;

    this.isLoading = true;
    link.classList.add("loading");

    try {
      // Reuse the surgical filter swap: it replaces the filters + product
      // grid + pagination in place and leaves the sidebar untouched, so the
      // sidebar's listeners and scroll position survive the switch.
      await applyCollectionFilter(url, { updateHistory: true });
      this.currentCollection = collectionHandle;
      this.updateActiveState(link);

      // Bring the shopper back to the top of the page for the new collection.
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error loading collection:", error);
      // Fallback to normal navigation
      window.location.href = url;
    } finally {
      this.isLoading = false;
      link.classList.remove("loading");
    }
  }

  updateActiveState(clickedLink) {
    // Remove active class from all sidebar items
    this.sidebarItems.forEach((item) => {
      item.classList.remove("active");
      item.closest(".collection-sidebar__item")?.classList.remove("active");
    });

    // Add active class to clicked item
    clickedLink.classList.add("active");
    clickedLink.closest(".collection-sidebar__item")?.classList.add("active");
  }
}

// ===== Load more / infinite scroll =====
let paginationObserver = null;

// Append nodes from a fetched grid, re-creating <script> tags so the
// product-data scripts actually execute.
function appendNodesWithScripts(sourceGrid, targetGrid) {
  Array.from(sourceGrid.childNodes).forEach((node) => {
    if (node.nodeType === 1 && node.tagName === "SCRIPT") {
      const s = document.createElement("script");
      s.textContent = node.textContent;
      targetGrid.appendChild(s);
      s.remove();
    } else {
      const clone = node.cloneNode(true);
      targetGrid.appendChild(clone);
      if (clone.querySelectorAll) {
        clone.querySelectorAll("script").forEach((oldScript) => {
          const s = document.createElement("script");
          s.textContent = oldScript.textContent;
          oldScript.replaceWith(s);
        });
      }
    }
  });
}

async function loadNextPage(pagination, grid) {
  if (!pagination || !grid || pagination.dataset.loading === "true") return;
  const nextUrl = pagination.getAttribute("data-next-url");
  if (!nextUrl) return;

  pagination.dataset.loading = "true";
  const button = pagination.querySelector("[data-load-more]");
  if (button) {
    button.classList.add("loading");
    button.disabled = true;
  }

  try {
    const response = await fetch(nextUrl);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");

    const newGrid = doc.querySelector("[data-products-grid]");
    if (newGrid) appendNodesWithScripts(newGrid, grid);

    const newPagination = doc.querySelector("[data-collection-pagination]");
    const newNextUrl = newPagination
      ? newPagination.getAttribute("data-next-url")
      : null;

    if (newNextUrl) {
      pagination.setAttribute("data-next-url", newNextUrl);
    } else {
      // No more pages: stop observing and remove the controls
      if (paginationObserver) paginationObserver.disconnect();
      pagination.remove();
    }

    if (window.initializeProductCards) window.initializeProductCards();
    if (window.initializeConversionButtons) window.initializeConversionButtons();
  } catch (error) {
    console.error("Error loading more products:", error);
  } finally {
    pagination.dataset.loading = "false";
    if (button) {
      button.classList.remove("loading");
      button.disabled = false;
    }
  }
}

function setupCollectionPagination() {
  if (paginationObserver) {
    paginationObserver.disconnect();
    paginationObserver = null;
  }

  const grid = document.querySelector("[data-products-grid]");
  const pagination = document.querySelector("[data-collection-pagination]");
  if (!grid || !pagination) return;

  const infinite = pagination.getAttribute("data-infinite") === "true";
  if (infinite && "IntersectionObserver" in window) {
    const sentinel = pagination.querySelector("[data-sentinel]");
    if (sentinel) {
      paginationObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) loadNextPage(pagination, grid);
        },
        { rootMargin: "600px 0px" }
      );
      paginationObserver.observe(sentinel);
    }
  }
}

// Delegated click for the Load more button (survives AJAX re-renders)
document.addEventListener("click", (e) => {
  const button = e.target.closest("[data-load-more]");
  if (!button) return;
  const pagination = button.closest("[data-collection-pagination]");
  const grid = document.querySelector("[data-products-grid]");
  loadNextPage(pagination, grid);
});

// ===== Storefront filters (Search & Discovery) =====
// Tracks whether any filter dropdown is open, so the scroll/resize handlers can
// short-circuit instead of running a querySelectorAll on every scroll frame.
let filterPanelsOpen = false;
// True while a panel is open as a bottom sheet (mobile) rather than a dropdown.
let filterSheetOpen = false;
let filterSheetTrigger = null;

// Mobile presents each filter group as a bottom sheet; desktop keeps the
// dropdown anchored to its button.
function useFilterSheet() {
  return window.matchMedia("(max-width: 767px)").matches;
}

function filterSheetBackdrop() {
  return document.querySelector("[data-filter-sheet-backdrop]");
}

// Keep Tab inside the open sheet, the way the cart drawer does.
function trapFilterSheetFocus(e) {
  if (e.key !== "Tab" || !filterSheetOpen) return;
  const panel = document.querySelector(
    '[data-filter-toggle][aria-expanded="true"]'
  )
    ?.closest("[data-filter-group]")
    ?.querySelector("[data-filter-panel]");
  if (!panel) return;

  const focusable = panel.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled])'
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

function closeFilterPanels() {
  document
    .querySelectorAll('[data-filter-toggle][aria-expanded="true"]')
    .forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
      const panel = toggle
        .closest("[data-filter-group]")
        ?.querySelector("[data-filter-panel]");
      if (panel) {
        panel.hidden = true;
        panel.removeAttribute("role");
        panel.removeAttribute("aria-modal");
        panel.removeAttribute("aria-label");
      }
    });

  if (filterSheetOpen) {
    const backdrop = filterSheetBackdrop();
    if (backdrop) backdrop.hidden = true;
    document.body.classList.remove("overlay-open");
    window.syncModalState?.();
    document.removeEventListener("keydown", trapFilterSheetFocus);
    filterSheetOpen = false;
    // Hand focus back to the button that opened the sheet.
    if (filterSheetTrigger && document.contains(filterSheetTrigger)) {
      filterSheetTrigger.focus();
    }
    filterSheetTrigger = null;
  }

  filterPanelsOpen = false;
}

function positionFilterPanel(toggle, panel) {
  const rect = toggle.getBoundingClientRect();
  panel.style.top = `${rect.bottom + 6}px`;
  const panelWidth = panel.offsetWidth || 210;
  let left = rect.left;
  const maxLeft = window.innerWidth - panelWidth - 8;
  if (left > maxLeft) left = maxLeft;
  if (left < 8) left = 8;
  panel.style.left = `${left}px`;
}

function openFilterPanel(toggle) {
  const group = toggle.closest("[data-filter-group]");
  const panel = group.querySelector("[data-filter-panel]");
  if (!panel) return;
  toggle.setAttribute("aria-expanded", "true");
  panel.hidden = false;
  filterPanelsOpen = true;

  if (!useFilterSheet()) {
    positionFilterPanel(toggle, panel);
    return;
  }

  // Sheet mode: drop any inline top/left left behind by the dropdown so the
  // CSS can pin the panel to the bottom of the viewport.
  panel.style.top = "";
  panel.style.left = "";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-label", group.dataset.filterLabel || "Filter");

  const backdrop = filterSheetBackdrop();
  if (backdrop) backdrop.hidden = false;
  document.body.classList.add("overlay-open");
  // The sheet reaches the bottom edge, so the app chrome underneath it — the
  // floating cart and the native tab bar — has to step aside (no-op on the web).
  window.syncModalState?.();
  filterSheetOpen = true;
  filterSheetTrigger = toggle;
  document.addEventListener("keydown", trapFilterSheetFocus);
  panel.querySelector("[data-filter-sheet-close]")?.focus();
}

function openFilterGroupByLabel(label) {
  if (!label) return;
  const group = document.querySelector(
    `[data-filter-group][data-filter-label="${CSS.escape(label)}"]`
  );
  if (!group) return;
  const toggle = group.querySelector("[data-filter-toggle]");
  if (toggle) openFilterPanel(toggle);
}

async function applyCollectionFilter(url, { updateHistory = true, openLabel = null } = {}) {
  if (!url) return;
  const section = document.querySelector(".collection-content");
  if (section) section.classList.add("loading");

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");

    // Swap the filter bar
    const newFilters = doc.querySelector("[data-collection-filters]");
    const curFilters = document.querySelector("[data-collection-filters]");
    if (newFilters && curFilters) curFilters.replaceWith(newFilters);
    else if (curFilters && !newFilters) curFilters.remove();

    // Swap product grid contents (re-running product-data scripts)
    const newGrid = doc.querySelector("[data-products-grid]");
    const curGrid = document.querySelector("[data-products-grid]");
    if (newGrid && curGrid) {
      curGrid.innerHTML = "";
      appendNodesWithScripts(newGrid, curGrid);
    }

    // Swap pagination controls
    const newPagination = doc.querySelector("[data-collection-pagination]");
    const curPagination = document.querySelector("[data-collection-pagination]");
    if (newPagination) {
      if (curPagination) curPagination.replaceWith(newPagination);
      else if (curGrid) curGrid.after(newPagination);
    } else if (curPagination) {
      curPagination.remove();
    }

    // Keep the header + document title in sync with the loaded collection
    // (e.g. when switching collections from the sidebar).
    const newHeaderTitle = doc.querySelector(".header__title");
    const curHeaderTitle = document.querySelector(".header__title");
    if (newHeaderTitle && curHeaderTitle) {
      curHeaderTitle.textContent = newHeaderTitle.textContent;
    }
    const newDocTitle = doc.querySelector("title");
    if (newDocTitle) document.title = newDocTitle.textContent;

    // Replace (not push) so browsing collections/filters doesn't stack a
    // history entry per click on the collection page.
    if (updateHistory) window.history.replaceState({ filtered: true }, "", url);

    if (window.initializeProductCards) window.initializeProductCards();
    if (window.initializeConversionButtons) window.initializeConversionButtons();
    setupCollectionPagination();

    // Keep the dropdown the shopper was using open (multi-select feel)
    openFilterGroupByLabel(openLabel);
  } catch (error) {
    console.error("Error applying filter:", error);
    window.location.href = url; // fallback to full navigation
  } finally {
    if (section) section.classList.remove("loading");
  }
}

// Filter bar interactions: toggle dropdowns, apply values, price, click-outside
document.addEventListener("click", (e) => {
  const toggle = e.target.closest("[data-filter-toggle]");
  if (toggle) {
    const isOpen = toggle.getAttribute("aria-expanded") === "true";
    closeFilterPanels();
    if (!isOpen) openFilterPanel(toggle);
    return;
  }

  const priceApply = e.target.closest("[data-price-apply]");
  if (priceApply) {
    const wrap = priceApply.closest("[data-price-filter]");
    const params = new URLSearchParams(window.location.search);
    const minVal = wrap.querySelector("[data-price-min]").value;
    const maxVal = wrap.querySelector("[data-price-max]").value;
    if (minVal) params.set(wrap.dataset.minName, minVal);
    else params.delete(wrap.dataset.minName);
    if (maxVal) params.set(wrap.dataset.maxName, maxVal);
    else params.delete(wrap.dataset.maxName);
    const qs = params.toString();
    applyCollectionFilter(window.location.pathname + (qs ? "?" + qs : ""), {
      openLabel: wrap.closest("[data-filter-group]")?.dataset.filterLabel,
    });
    return;
  }

  if (e.target.closest("[data-filter-sheet-close]")) {
    closeFilterPanels();
    return;
  }

  if (e.target.closest("[data-filter-sheet-backdrop]")) {
    closeFilterPanels();
    return;
  }

  const chip = e.target.closest("[data-filter-chip]");
  if (chip) {
    e.preventDefault();
    applyCollectionFilter(chip.getAttribute("href"), {
      openLabel: chip.closest("[data-filter-group]")?.dataset.filterLabel,
    });
    return;
  }

  // Click outside any open panel closes them
  if (!e.target.closest("[data-filter-panel]")) closeFilterPanels();
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeFilterPanels();
});

// Filter panels are fixed-positioned, so close them when the page or the
// filter bar scrolls, or on resize (but not when scrolling inside a panel).
// All guarded by filterPanelsOpen so nothing runs while no panel is open.
window.addEventListener("resize", () => {
  if (!filterPanelsOpen) return;
  // The sheet is pinned to the viewport, so it survives resizes — including the
  // one the on-screen keyboard fires while typing in the price fields. It does
  // not survive crossing back to desktop, where the panel is a dropdown that
  // needs its anchor position.
  if (!filterSheetOpen || !useFilterSheet()) closeFilterPanels();
});
window.addEventListener(
  "scroll",
  () => {
    if (filterPanelsOpen && !filterSheetOpen) closeFilterPanels();
  },
  { passive: true }
);
document.addEventListener(
  "scroll",
  (e) => {
    if (!filterPanelsOpen) return;
    if (
      e.target.nodeType === 1 &&
      e.target.matches &&
      e.target.matches("[data-collection-filters]")
    ) {
      closeFilterPanels();
    }
  },
  true
);

// Keep filters in sync with browser back/forward
window.addEventListener("popstate", () => {
  if (window.location.pathname.includes("/collections/")) {
    applyCollectionFilter(window.location.href, { updateHistory: false });
  }
});

// Keep the sticky offset (sidebar + filter bar) in sync with the actual
// sticky header height, which varies by store and by the Addressly bar.
function updateCollectionStickyOffset() {
  const header = document.querySelector(
    ".shopify-section-group-header-group"
  );
  const height = header
    ? Math.round(header.getBoundingClientRect().height)
    : 70;
  document.documentElement.style.setProperty(
    "--collection-sticky-top",
    `${height}px`
  );
}

// Initialize Collection class when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  // Only initialize on collection pages
  if (window.location.pathname.includes("/collections/")) {
    updateCollectionStickyOffset();
    new Collection();
    setupCollectionPagination();

    const header = document.querySelector(
      ".shopify-section-group-header-group"
    );
    if (header && "ResizeObserver" in window) {
      new ResizeObserver(updateCollectionStickyOffset).observe(header);
    } else {
      // Fallback only where ResizeObserver is unavailable — otherwise the
      // observer already covers header height changes (incl. from resizes),
      // so a separate resize listener would just double the layout reads.
      window.addEventListener("resize", updateCollectionStickyOffset);
    }
  }
});
